import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TablePagination,
  IconButton,
  CircularProgress,
  Alert,
  Menu,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ViewAgendaIcon from '@mui/icons-material/ViewAgenda';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate } from "react-router-dom";
import { InfoBox } from "../../components/InfoBox";
import { LoadingPlanCard } from "../../components/LoadingPlanCard";
import { TableComponent, TableColumn } from "../../components/ui/TableComponent";
import { useLoadingPlan, formatLoadingPlanForDisplay } from "../../contexts/LoadingPlanContext";
import { loadingPlansService } from "../../services";
import { LoadingPlanStatus, LoadingPlanStatusLabels } from "../../types/loadingPlan";
import { ROUTES } from "../../consts/routes";
import "./LoadingPlansPage.scss";

const LoadingPlansPage: React.FC = () => {
  const [view, setView] = React.useState("list");
  const [sortBy, setSortBy] = React.useState<string>("created_at");
  const [sortOrder] = React.useState<'ASC' | 'DESC'>('DESC');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [total, setTotal] = React.useState(0);
  const [info, setInfo] = React.useState({ activeJobs: 0, draftPlans: 0, avgUtilization: 0 });
  const [searchQuery, setSearchQuery] = React.useState("");
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedPlanId, setSelectedPlanId] = React.useState<number | null>(null);
  const { loadingPlans, loading, error, refetchLoadingPlans } = useLoadingPlan();
  const navigate = useNavigate();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, planId: number) => {
    setAnchorEl(event.currentTarget);
    setSelectedPlanId(planId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPlanId(null);
  };

  const handleMenuAction = (action: 'view' | 'continue') => {
    if (selectedPlanId !== null) {
      if (action === 'view') {
        handleViewPlan(selectedPlanId);
      } else if (action === 'continue') {
        handleContinuePlan(selectedPlanId);
      }
    }
    handleMenuClose();
  };

  const handlePublishPlan = async () => {
    if (selectedPlanId !== null) {
      await loadingPlansService.updateLoadingPlan(selectedPlanId, {
        status: LoadingPlanStatus.PUBLISHED
      });
      fetchPlans();
    }
    handleMenuClose();
  };

  const handleCopyUrl = async () => {
    if (selectedPlanId !== null) {
      const url = await loadingPlansService.getSharedUrl(selectedPlanId);
      navigator.clipboard.writeText(url.shareUrl);
    }
    handleMenuClose();
  };

  // Define table columns for grid view
  const tableColumns: TableColumn<any>[] = [
    { id: 'name', label: 'Project', render: (plan) => formatLoadingPlanForDisplay(plan).name },
    { id: 'trailer', label: 'Trailer', render: (plan) => formatLoadingPlanForDisplay(plan).trailer },
    { id: 'parts', label: 'Parts', render: (plan) => formatLoadingPlanForDisplay(plan).parts },
    { id: 'createdShort', label: 'Created', render: (plan) => formatLoadingPlanForDisplay(plan).createdShort },
    { id: 'shipDate', label: 'Ship Date', render: (plan) => formatLoadingPlanForDisplay(plan).shipDate },
    { id: 'weight', label: 'Weight', render: (plan) => formatLoadingPlanForDisplay(plan).weight },
    {
      id: 'status',
      label: 'Status',
      render: (plan) => (
        <Chip
          label={LoadingPlanStatusLabels[plan.status as LoadingPlanStatus]}
          color={
            plan.status === LoadingPlanStatus.PUBLISHED
              ? "success"
              : plan.status === LoadingPlanStatus.READY
                ? "info"
                : plan.status === LoadingPlanStatus.FAILED
                  ? "error"
                  : "default"
          }
          variant="outlined"
          className="status-chip-table"
          sx={
            plan.status === LoadingPlanStatus.DRAFT
              ? {
                backgroundColor: "rgba(0, 0, 0, 0.08)",
                borderColor: "rgba(0, 0, 0, 0.08)",
              }
              : {}
          }
        />
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      render: (plan) => (
        <IconButton onClick={(e) => handleMenuOpen(e, plan.id)}>
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  const handleViewChange = (_: any, nextView: string) => {
    if (nextView) setView(nextView);
  };

  const handleSortChange = (event: any) => {
    const newSortBy = event.target.value;
    setSortBy(newSortBy);
    setPage(0);
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleCreateNewPlan = () => {
    navigate(ROUTES.createLoadingPlan);
  };

  const handleContinuePlan = (planId: number) => {
    navigate(`${ROUTES.loadingPlans}/${planId}`);
  };

  const handleViewPlan = (planId: number) => {
    navigate(`${ROUTES.loadingPlans}/view/${planId}`);
  };

  const fetchPlans = async () => {
    const response = await refetchLoadingPlans({
      limit: rowsPerPage,
      offset: page * rowsPerPage,
      search: searchQuery || undefined,
      sortBy,
      sortOrder
    });
    if (response) {
      setTotal(response.total);
    }
  };

  const fetchInfo = async () => {
    try {
      const infoResponse = await loadingPlansService.fetchInfo();
      setInfo({
        activeJobs: infoResponse.activeJobs,
        draftPlans: infoResponse.draftPlans,
        avgUtilization: Math.round(infoResponse.avgUtilization),
      });
    } catch (e) {
      console.error('Failed to fetch loading plan info:', e);
    }
  };

  useEffect(() => {
    fetchInfo();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPlans();
    }, 300);

    return () => clearTimeout(timer);
  }, [page, rowsPerPage, searchQuery, sortBy, sortOrder]);

  return (
    <Box className="loading-plans-page">
      {/* Header */}
      <Box className="lp-toolbar">
        <Typography className="lp-title">
          Loading Plans
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleCreateNewPlan}
        >
          NEW LOADING PLAN
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Stats */}
      <Box className="info-row">
        <InfoBox label="Active jobs" value={info.activeJobs} />
        <InfoBox label="Draft plans" value={info.draftPlans} />
        <InfoBox label="Avg. Utilization" value={`${info.avgUtilization}%`} />
      </Box>

      {/* Search & Sort */}
      <Box className="search-sort">
        <TextField
          placeholder="Search load, sequence, truck #"
          size="small"
          variant="outlined"
          className="search-input-wrapper"
          sx={{ flex: 1, mr: 2 }}
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
          }}
        />
        <FormControl size="small" className="sort-select">
          <InputLabel id="sort-by-label">Sort by</InputLabel>
          <Select
            labelId="sort-by-label"
            value={sortBy}
            label="Sort by"
            onChange={handleSortChange}
          >
            <MenuItem value="title">Name</MenuItem>
            <MenuItem value="created_at">Created Date</MenuItem>
            <MenuItem value="ship_date">Ship Date</MenuItem>
            <MenuItem value="status">Status</MenuItem>
          </Select>
        </FormControl>
        <ToggleButtonGroup value={view} exclusive onChange={handleViewChange} size="small" className="view-toggle">
          <ToggleButton value="list">
            <ViewAgendaIcon />
          </ToggleButton>
          <ToggleButton value="grid">
            <ViewColumnIcon />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Plans List - Card View */}
          {view === "list" && (
            <Box className="plans-list">
              {(loadingPlans || []).map((plan, index) => (
                <LoadingPlanCard key={index} plan={formatLoadingPlanForDisplay(plan)} onContinue={handleContinuePlan} onView={handleViewPlan} fetchPlans={fetchPlans} />
              ))}
            </Box>
          )}

          {/* Plans List - Table View */}
          {view === "grid" && (
            <Box className="table-container-wrapper">
              <TableComponent
                data={loadingPlans || []}
                columns={tableColumns}
                className="table-container"
              />
            </Box>
          )}

          {/* Action Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            {(loadingPlans?.find(p => p.id === selectedPlanId)?.status === LoadingPlanStatus.DRAFT ||
              loadingPlans?.find(p => p.id === selectedPlanId)?.status === LoadingPlanStatus.IN_PROGRESS) && (
              <MenuItem onClick={() => handleMenuAction('continue')}>Continue</MenuItem>
            )}
            {loadingPlans?.find(p => p.id === selectedPlanId)?.status === LoadingPlanStatus.READY && [
              <MenuItem key="edit" onClick={() => handleMenuAction('continue')}>Edit</MenuItem>,
              <MenuItem key="publish" onClick={handlePublishPlan}>Publish</MenuItem>,
            ]}
            {(loadingPlans?.find(p => p.id === selectedPlanId)?.status === LoadingPlanStatus.PUBLISHED ||
              loadingPlans?.find(p => p.id === selectedPlanId)?.status === LoadingPlanStatus.LOADED) && [
                <MenuItem key="view" onClick={() => handleMenuAction('view')}>View</MenuItem>,
                <MenuItem key="copyUrl" onClick={handleCopyUrl}>Copy URL</MenuItem>,
              ]}
            {loadingPlans?.find(p => p.id === selectedPlanId)?.status === LoadingPlanStatus.FAILED && (
              <MenuItem onClick={() => handleMenuAction('continue')}>Retry</MenuItem>
            )}
          </Menu>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </>
      )}
    </Box>
  );
};

export { LoadingPlansPage };